/**
 * One button-shaped backend operation on the system console.
 *
 * Every scan / schema / export endpoint answers the same envelope shape
 * (`{status: 'ok' | 'partial', result: …}`), so the card is generic over the
 * response type and takes three view functions: a German one-line summary, the
 * server's own error array (rendered as a bullet list — 'partial' means "some
 * items failed", not "the call failed"), and the raw JSON, which stays available
 * in a collapsed `<details>` because these results are operational evidence.
 *
 * Being disabled is a first-class state: `disabledReason` is shown as the native
 * tooltip, which is how an auditor (or an admin without `scan.run`) sees WHY a
 * control is dead instead of guessing.
 */

import { useState, type ReactNode } from 'react';

import { Banner } from '../../components/Banner';
import Spinner from '../../components/Spinner';
import { errorMessage } from '../../lib/api';

export interface OperationCardProps<T> {
  title: string;
  /** German explanation of what the call does — and what it writes. */
  description: ReactNode;
  buttonLabel: string;
  /** Label while the request is in flight. */
  runningLabel?: string;
  /** The api.ts wrapper to call. */
  run: () => Promise<T>;
  /** German one-line summary of a successful response. */
  summarise: (value: T) => ReactNode;
  /** The server-reported per-item errors, if the response carries any. */
  errorsOf?: (value: T) => readonly string[];
  /** Non-null disables the button and explains why (native tooltip). */
  disabledReason?: string | null;
  /** Red framing for destructive operations. */
  destructive?: boolean;
  /** Extra controls above the button, e.g. a confirmation input. */
  children?: ReactNode;
  /** Called after a successful response with the server's error list. */
  onDone?: (value: T, errors: readonly string[]) => void;
  /** Called with the German detail when the request itself failed. */
  onFailed?: (detail: string) => void;
}

export function OperationCard<T>({
  title,
  description,
  buttonLabel,
  runningLabel = 'Läuft…',
  run,
  summarise,
  errorsOf,
  disabledReason = null,
  destructive = false,
  children,
  onDone,
  onFailed,
}: OperationCardProps<T>): ReactNode {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [finishedAt, setFinishedAt] = useState<Date | null>(null);

  const disabled = running || disabledReason !== null;

  async function onClick(): Promise<void> {
    setRunning(true);
    setError(null);
    try {
      const value = await run();
      setResult(value);
      setFinishedAt(new Date());
      const errors = errorsOf?.(value) ?? [];
      onDone?.(value, errors);
    } catch (err) {
      // German server detail verbatim (403 permissions, 502/503 upstream, …).
      const detail = errorMessage(err);
      setError(detail);
      setResult(null);
      onFailed?.(detail);
    } finally {
      setRunning(false);
    }
  }

  const serverErrors = result === null ? [] : (errorsOf?.(result) ?? []);

  return (
    <section className={destructive ? 'ops-card ops-destructive' : 'ops-card'}>
      <h3>{title}</h3>
      <div className="ops-desc">{description}</div>
      {children}
      <div className="ops-actions">
        <button
          type="button"
          className={destructive ? 'danger' : 'primary'}
          onClick={() => void onClick()}
          disabled={disabled}
          title={disabledReason ?? undefined}
        >
          {running ? <Spinner variant="on-accent" label={runningLabel} /> : buttonLabel}
        </button>
        {running ? <span className="muted">{runningLabel}</span> : null}
        {!running && finishedAt !== null ? (
          <span className="muted" style={{ fontSize: 'var(--text-sm)' }}>
            zuletzt {finishedAt.toLocaleTimeString('de-DE')}
          </span>
        ) : null}
      </div>

      {error !== null ? <Banner kind="error">{error}</Banner> : null}

      {result !== null ? (
        <>
          <Banner
            kind={serverErrors.length > 0 ? 'warn' : 'ok'}
            items={serverErrors.length > 0 ? serverErrors : undefined}
          >
            <span className="ops-summary">{summarise(result)}</span>
          </Banner>
          <details className="ops-json">
            <summary>Rohdaten (JSON)</summary>
            <pre>{JSON.stringify(result, null, 2)}</pre>
          </details>
        </>
      ) : null}
    </section>
  );
}

export default OperationCard;
